import { useEffect, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';

import { useAppearance } from '../design-system/providers/appearance';
import i18n from './index';

/**
 * Binds the i18next instance to the locale `AppearanceProvider` owns.
 *
 * This component subscribes; it does not own. `locale` has one source of truth,
 * and `lang` / `dir` have one writer — anything else lets the direction of the
 * page and the language of its words disagree.
 *
 * Must be mounted *inside* `AppearanceProvider`; `useAppearance` throws
 * otherwise, which is the failure we want (a silent English fallback is the bug
 * this task exists to fix).
 *
 * Why `useEffect` and not `useLayoutEffect`: the provider stamps `dir` from a
 * passive effect too, so both settle in the same flush and the user sees one
 * repaint carrying Arabic words *and* an RTL shell. A layout effect here would
 * paint Arabic words inside a still-LTR shell for a frame — worse, not better.
 * The reload path never reaches this effect at all: `./index` seeds i18next from
 * the stored preference at module load.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const { locale } = useAppearance();

  useEffect(() => {
    if (i18n.language === locale) return;
    void i18n.changeLanguage(locale);
  }, [locale]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
